import { Component, ViewChild } from '@angular/core';

import { ThfDisclaimerGroup, ThfDynamicFieldType, ThfDynamicFormField, ThfPageFilter } from '@totvs/thf-ui';

import { getBrowserLanguage } from '../../utils/util';

import { ThfAdvancedFilterComponent } from './thf-advanced-filter/thf-advanced-filter.component';
import { ThfPageDynamicSearchBaseComponent } from './thf-page-dynamic-search-base.component';

/**
 * @docsExtends ThfPageDynamicSearchBaseComponent
 *
 * @example
 *
 * <example name="thf-page-dynamic-search-basic" title="Totvs Page Dynamic Search Basic">
 *  <file name="sample-thf-page-dynamic-search-basic/sample-thf-page-dynamic-search-basic.component.html"> </file>
 *  <file name="sample-thf-page-dynamic-search-basic/sample-thf-page-dynamic-search-basic.component.ts"> </file>
 * </example>
 *
 * <example name="thf-page-dynamic-search-hiring-processes" title="Totvs Page Dynamic Search - Hiring processes">
 *  <file name="sample-thf-page-dynamic-search-hiring-processes/sample-thf-page-dynamic-search-hiring-processes.component.html"> </file>
 *  <file name="sample-thf-page-dynamic-search-hiring-processes/sample-thf-page-dynamic-search-hiring-processes.component.ts"> </file>
 *  <file name="sample-thf-page-dynamic-search-hiring-processes/sample-thf-page-dynamic-search-hiring-processes.service.ts"> </file>
 * </example>
 */
@Component({
  selector: 'thf-page-dynamic-search',
  templateUrl: './thf-page-dynamic-search.component.html'
})
export class ThfPageDynamicSearchComponent extends ThfPageDynamicSearchBaseComponent {

  private readonly _disclaimerGroup: ThfDisclaimerGroup = {
    change: this.onChangeDisclaimerGroup.bind(this),
    disclaimers: [],
    title: this.literals.disclaimerGroupTitle
  };

  private readonly _filterSettings: ThfPageFilter = {
    action: 'onAction',
    advancedAction: 'onAdvancedAction',
    ngModel: 'quickFilter',
    placeholder: this.literals.filterSettingsPlaceholder
  };

  // Flag to control when changeDisclaimerGroup should be called
  private changeDisclaimersEnabled: boolean = false;

  private quickFilter;

  @ViewChild(ThfAdvancedFilterComponent, { static: true }) thfAdvancedFilter: ThfAdvancedFilterComponent;

  get disclaimerGroup() {
    return Object.assign({}, this._disclaimerGroup);
  }

  get filterSettings() {
    this._filterSettings.advancedAction = this.filters.length === 0 ? undefined : 'onAdvancedAction';

    return Object.assign({}, this._filterSettings);
  }

  onAction() {
    this.changeDisclaimersEnabled = false;
    this._disclaimerGroup.disclaimers = [
      { property: 'search', label: `${this.literals.quickSearchLabel} ${this.quickFilter}`, value: this.quickFilter }
    ];

    if (this.quickSearch.observers && this.quickSearch.observers.length > 0) {
      this.quickSearch.emit(this.quickFilter);
    }

    this.quickFilter = undefined;
  }

  onAdvancedAction() {
    this.thfAdvancedFilter.open();
  }

  onAdvancedSearch(filters) {
    this.changeDisclaimersEnabled = false;
    this._disclaimerGroup.disclaimers = this.setDisclaimers(filters);

    this.advancedSearch.emit(filters);
  }

  private formatDate(date: string) {
    const year = parseInt(date.substr(0, 4), 10);
    const month = parseInt(date.substr(5, 2), 10);
    const day = parseInt(date.substr(8, 2), 10);

    return new Date(year, month - 1, day).toLocaleDateString(getBrowserLanguage());
  }

  private getFieldByProperty(fields: Array<ThfDynamicFormField>, fieldName: string) {
    return fields.find((field: ThfDynamicFormField) => field.property === fieldName);
  }

  private onChangeDisclaimerGroup(disclaimers) {
    this.changeDisclaimersEnabled ? this.changeDisclaimers.emit(disclaimers) : this.changeDisclaimersEnabled = true;
  }

  private setDisclaimers(filters) {
    const disclaimers = [];

    Object.keys(filters).forEach(filter => {
      const field = this.getFieldByProperty(this.filters, filter);

      const value = field.type === ThfDynamicFieldType.Date ? this.formatDate(filters[filter]) : filters[filter];

      disclaimers.push({
        label: `${field.label}: ${value}`,
        property: filter,
        value: filters[filter]
      });
    });

    return disclaimers;
  }

}
